import { Injectable }                       from '@angular/core';
import { ARCHIVING_EXPORT_METADATA }        from '@app/modules/archiving/config/archiving-export.config';
import {
	ArchivingExport,
	CreateArchivingExportBody
}                                           from '@app/modules/archiving/models/archiving-export.model';
import {
	ArchivingExportsDetailPageRoute,
	ArchivingRoutesEnum
}                                           from '@app/modules/archiving/routes/archiving.routes';
import { IPropsUpdateArchivingExportState } from '@app/modules/archiving/store/archiving-export/archiving-export.actions';
import { archivingExportStore }             from '@app/modules/archiving/store/archiving-export/archiving-export.store';
import { BusinessPremisesRoutesEnum }       from '@app/modules/business-premises/routes/business-premises.routes';
import { AbstractBaseService }              from '@core/bases/services/abstractBaseService';
import {
	IArchivingExportHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                           from '@core/interfaces/api.interface';
import { AllowedDetailsTabEnum }            from '@core/interfaces/common.interfaces';
import { RouteBasePath }                    from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	StoreActionsEnum
}                                           from '@core/interfaces/store.interface';
import { ArchivingExportApiService }        from '@http/archiving/archiving-export-api.service';
import { Store }                            from '@ngrx/store';
import { FilterIdentifiersEnum }            from '@shared/list-operations/model/filter.model';
import { ListOperationsIdentifierEnum }     from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                     from '@shared/services/toaster.service';
import { DateUtils }                        from '@utils/helpers';
import { Observable }                       from 'rxjs';
import {
	map,
	take
}                                           from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class ArchivingExportService extends AbstractBaseService {

	constructor(
		private readonly archivingExportApiService: ArchivingExportApiService,
		protected readonly store$: Store
	) {
		super( store$, archivingExportStore.selectors, archivingExportStore.actions );
		this.logger.trace( 'ArchivingExportService constructor' );
	}

	private prepareApiParams( params: IArchivingExportHttpParams, businessPremisesId: string ): IArchivingExportHttpParams {
		const resultParams: IArchivingExportHttpParams = { ...this.generateParams( params ), businessPremisesId };
		const userLocale: string = this.uiService.getLanguage( true );

		if ( params && params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ] ) {
			if ( params.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 0 ] ) {
				resultParams.createdDateFrom = DateUtils.formatDateToLocale(
					params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 0 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
			if ( params.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 1 ] ) {
				resultParams.createdDateTo = DateUtils.formatDateToLocale(
					params?.activeFilters[ FilterIdentifiersEnum.CREATED_DATE ]?.param[ 1 ], DateUtils.ISO_DATE_FORMAT, userLocale
				);
			}
		}

		return resultParams;
	}

	private extendExport( exportId: string, caller: string ): void {
		const dialogConfirmationText = this.translationService.getI18nString(
			true,
			ARCHIVING_EXPORT_METADATA.i18n.basepath,
			'entity.extendDialog.description'
		);
		this.dialogCommonService.showConfirmationDialog( dialogConfirmationText )
		.pipe( take( 1 ) )
		.subscribe( () => {
			this.uiService.showPageLoaderAndCountUp();
			this.extend( exportId )
			.pipe( take( 1 ) )
			.subscribe( ( archivingExportState: RequestCompleteEntityState<ArchivingExport> ) => {
				if ( archivingExportState.requestState === RequestState.SUCCESS ) {
					this.read( exportId ).pipe( take( 1 ) ).subscribe();
				}
				this.toasterService.dispatch( archivingExportState, ARCHIVING_EXPORT_METADATA.entityId, actionStatus.EXTEND, true );
				this.uiService.hidePageLoaderOrCountDown();
			} );
		} );
	}

	private downloadExportContainer( archivingExport: ArchivingExport ): void {
		this.logger.info( 'Download container ' + archivingExport.id );
		if ( archivingExport.result?.url ) {
			window.open( archivingExport.result.url, '_self' );
		}
	}

	// ARCHIVING EXPORT SPECIFIC SELECTORS ###########################################################################

	// ARCHIVING EXPORT SPECIFIC ACTIONS ###########################################################################
	updateStoreState( updateArchivingExportState: IPropsUpdateArchivingExportState ): void {
		this.store$.dispatch( archivingExportStore.actions.updateArchivingExportState( updateArchivingExportState ) );
	}

	// Entity Actions ##############################################################################

	archivingExportDownloadOnDetailPage( archivingExport: ArchivingExport ): void {
		this.downloadExportContainer( archivingExport );
	}

	archivingExportDownloadOnEntity( archivingExport: ArchivingExport ): void {
		this.downloadExportContainer( archivingExport );
	}

	archivingExportExtendOnDetailPage( archivingExportId: string, caller: string ): void {
		this.extendExport( archivingExportId, caller );
	}

	archivingExportExtendOnEntity( archivingExportId: string, caller: string ): void {
		this.extendExport( archivingExportId, caller );
	}

	// NAVIGATION #################################################################################
	navigateBack( caller = '', shouldRestoreListOperationData = true ): void {
		this.listOperationsService.state.listOperationsSetRestoreFlag(
			ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_ARCHIVE_EXPORTS,
			shouldRestoreListOperationData,
			caller
		);

		this.routerService.navigateBack(
			this.routerService.getFallbackRoute( RouteBasePath.BusinessPremises, BusinessPremisesRoutesEnum.ListPage ),
			caller + ' | ArchivingExportService:navigateBack'
		);
	}

	navigateToExportDetailPage( archivingExportId: string ): void {
		if ( archivingExportId ) {
			const routeDefinition: ArchivingExportsDetailPageRoute = {
				basePath: RouteBasePath.ArchivingExports,
				path: ArchivingRoutesEnum.ArchivingExportsDetailPage,
				params: {
					archivingExportId,
					tab: AllowedDetailsTabEnum.ARCHIVING_EXPORT_ITEMS
				}
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ArchivingExportService:navigateToExportsDetailPage'
			);
		}
	}

	navigateToExportDetailPageTab( archivingExportId: string, tab: AllowedDetailsTabEnum ): void {
		if ( archivingExportId && tab ) {
			const routeDefinition: ArchivingExportsDetailPageRoute = {
				basePath: RouteBasePath.ArchivingExports,
				path: ArchivingRoutesEnum.ArchivingExportsDetailPage,
				params: { archivingExportId, tab }
			};

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ArchivingExportService:navigateToExportDetailPageTab',
				true
			);
		}
	}

	// API #################################################################################
	searchWithStoredListOperationsData(
		businessPremisesId: string,
		listOperationsId: ListOperationsIdentifierEnum,
		caller: string
	): Observable<RequestCompleteListState<ArchivingExport>> {
		return this.listOperationsService.helper.requestWithStoredListOperationsData<ArchivingExport, IArchivingExportHttpParams>(
			( [ businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ] ) =>
				this.search( businessPremisesIdArg, listOperationsArg, callerArg, storeConfigArg ),
			[ businessPremisesId, `${ caller } | ArchivingImportService:searchWithStoredListOperationsData`, null ],
			1,
			listOperationsId
		);
	}

	search(
		businessPremisesId: string,
		params?: IArchivingExportHttpParams,
		caller?: string,
		storeConfig?: IStoreConfig
	): Observable<RequestCompleteListState<ArchivingExport>> {
		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		let storeProcessingConfig = this.createListStoreProcessingConfig<ArchivingExport>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeList$<ArchivingExport>(),
			StoreActionsEnum.SET_ALL
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingExportService:search` };

		params = { ...this.prepareApiParams( params, businessPremisesId ) };

		const req$ = this.archivingExportApiService.search( params );
		return this.requestListThroughProxy<ArchivingExport>( req$, storeProcessingConfig )
		.pipe(
			take( 1 ),
			map( ( archivingExportState: RequestCompleteListState<ArchivingExport> ) => {
				if ( archivingExportState.requestState === RequestState.SUCCESS ) {
					let updatedEntities: ArchivingExport[] = [ ...archivingExportState.list ];
					updatedEntities = updatedEntities.map( ( entity: ArchivingExport ) => {
						if ( entity?.expiration ) {
							const daysLeftUntilExpire: number =
								DateUtils.calculateDifferenceToCurrent( entity.expiration, DateUtils.DatePrecisionLevelEnum.DAY );
							const updatedEntity: ArchivingExport = { ...entity, ui: { daysLeftUntilExpire } };
							return updatedEntity;
						}
						return entity;
					} );
					this.storeUpsertMany( updatedEntities, `${ caller } | ArchivingExportService:search->response` );

					archivingExportState = {
						...archivingExportState,
						list: updatedEntities
					};
				}
				return archivingExportState;
			} )
		);
	}

	read( id: string, withFullResponse?: boolean, caller?: string, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ArchivingExport>> {
		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		let storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingExport>(
			storeConfig,
			this.storeEntityById$<ArchivingExport>( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		storeProcessingConfig = { ...storeProcessingConfig, caller: `${ caller } | ArchivingExportService:read` };

		const req$ = this.archivingExportApiService.read( id, withFullResponse );
		return this.requestEntityThroughProxy<ArchivingExport>( id, req$, storeProcessingConfig )
		.pipe(
			take( 1 ),
			map( ( archivingExportState: RequestCompleteEntityState<ArchivingExport> ) => {

				const entity: ArchivingExport = archivingExportState?.entity;

				if ( archivingExportState.requestState === RequestState.SUCCESS && entity?.expiration ) {
					const daysLeftUntilExpire: number =
						DateUtils.calculateDifferenceToCurrent( entity.expiration, DateUtils.DatePrecisionLevelEnum.DAY );
					const updatedEntity: ArchivingExport = { ...entity, ui: { daysLeftUntilExpire } };
					this.storeUpsertOne( updatedEntity );

					archivingExportState = {
						...archivingExportState,
						entity: updatedEntity
					};
				}
				return archivingExportState;
			} )
		);
	}

	createExports( createExportRequest: CreateArchivingExportBody, businessPremisesId: string ): Observable<RequestCompleteEntityState<ArchivingExport>> {
		const req$ = this.archivingExportApiService.create( createExportRequest, businessPremisesId );
		return this.requestEntityThroughProxy<ArchivingExport>( undefined, req$ );
	}

	extend( exportId: string, storeConfig?: IStoreConfig, throwError = false ):
		Observable<RequestCompleteEntityState<ArchivingExport>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<ArchivingExport>(
			storeConfig,
			null,
			StoreActionsEnum.UPDATE_ONE
		);

		const req$ = this.archivingExportApiService.extendExport( exportId, throwError );
		return this.requestEntityThroughProxy( exportId, req$, storeProcessingConfig );
	}
}
